import { getSupabaseClient } from "@veorest/api-client";
import type { TranslationKey } from "../i18n";

// =============================================================================
// Multi-wallet v2 (Sprint S3) — partner wallet, client dues, driver settlement.
// RPCs shipped in migrations 176 (partner_wallet_get) + 178 (S3 read models).
// Fee-only rule: wallets track the 250 IQD platform fee + delivery settlements.
// =============================================================================

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function str(v: unknown, fallback = ""): string {
  if (v == null) return fallback;
  return String(v);
}

function nullableStr(v: unknown): string | null {
  return v == null ? null : String(v);
}

function num(v: unknown, fallback = 0): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function bool(v: unknown): boolean {
  return v === true;
}

// ── Partner wallet (consolidated) ─────────────────────────────────────────────

export interface PartnerWalletV2 {
  balance: number;
  currency: string;
  negative_credit_limit: number;
  alert_threshold_iqd: number;
  updated_at: string | null;
}

export interface PartnerWalletV2Transaction {
  id: string;
  order_id: string | null;
  type: string;
  amount: number;
  balance_after: number;
  description: string | null;
  created_at: string;
  /** 246: component cleared by the entry (delivery | order | null = net). */
  scope: string | null;
}

export interface PartnerWalletV2Totals {
  tx_count: number;
  fees_total: number;
  topups_total: number;
}

export interface PartnerWalletV2Snapshot {
  wallet: PartnerWalletV2;
  transactions: PartnerWalletV2Transaction[];
  totals: PartnerWalletV2Totals;
}

function mapWallet(raw: Record<string, unknown> | null): PartnerWalletV2 {
  return {
    balance: num(raw?.balance),
    currency: str(raw?.currency, "IQD"),
    negative_credit_limit: num(raw?.negative_credit_limit),
    alert_threshold_iqd: num(raw?.alert_threshold_iqd, 10000),
    updated_at: nullableStr(raw?.updated_at),
  };
}

function mapTransactions(value: unknown): PartnerWalletV2Transaction[] {
  const list = Array.isArray(value) ? value : [];
  return list
    .map((item) => asRecord(item))
    .filter((r): r is Record<string, unknown> => !!r)
    .map((r) => ({
      id: str(r.id),
      order_id: nullableStr(r.order_id),
      type: str(r.type),
      amount: num(r.amount),
      balance_after: num(r.balance_after),
      description: nullableStr(r.description),
      created_at: str(r.created_at),
      scope: nullableStr(r.scope),
    }));
}

function mapTotals(value: unknown): PartnerWalletV2Totals {
  const row = asRecord(value);
  return {
    tx_count: num(row?.tx_count),
    fees_total: num(row?.fees_total),
    topups_total: num(row?.topups_total),
  };
}

/** Consolidated partner wallet snapshot (balance + last 50 transactions). */
export async function fetchPartnerWalletV2(
  partnerId: string
): Promise<PartnerWalletV2Snapshot> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_wallet_get", {
    p_partner_id: partnerId,
  });
  if (error) throw error;
  const row = asRecord(data) ?? {};
  return {
    wallet: mapWallet(asRecord(row.wallet)),
    transactions: mapTransactions(row.transactions),
    totals: mapTotals(row.totals),
  };
}

// ── Client wallet (v2) — dues visibility for D6 transfers ─────────────────────

export interface PartnerClientWalletV2 extends PartnerWalletV2Snapshot {
  client_type: string;
  controlling_partner_id: string | null;
  billing_consolidated: boolean;
  dues_unsettled: boolean;
}

/** Client fee-wallet snapshot + dues/consolidation flags (partner-safe RPC). */
export async function fetchPartnerClientWalletV2(
  partnerId: string,
  tenantId: string
): Promise<PartnerClientWalletV2> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_client_wallet_v2_get", {
    p_partner_id: partnerId,
    p_tenant_id: tenantId,
  });
  if (error) throw error;
  const row = asRecord(data) ?? {};
  return {
    wallet: mapWallet(asRecord(row.wallet)),
    transactions: mapTransactions(row.transactions),
    totals: mapTotals(row.totals),
    client_type: str(row.client_type),
    controlling_partner_id: nullableStr(row.controlling_partner_id),
    billing_consolidated: bool(row.billing_consolidated),
    dues_unsettled: bool(row.dues_unsettled),
  };
}

// ── Client ledger (245, v2 by 250) — delivery-fees dues rail ─────────────────

export interface PartnerClientLedgerEntry {
  order_id: string;
  display_number: string | null;
  status: string;
  return_to_store: boolean;
  created_at: string;
  completed_at: string | null;
  rejected_at: string | null;
  delivery_fee: number;
  delivery_fee_responsibility: string | null;
  delivery_fee_cash_to_driver: boolean;
  due_amount: number;
}

export interface PartnerClientPayment {
  id: string;
  amount: number;
  note: string | null;
  created_at: string;
}

export interface PartnerClientLedgerTotals {
  orders: number;
  delivery_due_total: number;
  rejected_due_total: number;
  fees_total: number;
  paid_total: number;
  balance_to_pay: number;
}

export interface PartnerClientLedger {
  tenant_id: string;
  totals: PartnerClientLedgerTotals;
  entries: PartnerClientLedgerEntry[];
  payments: PartnerClientPayment[];
}

/** Client⇄partner dues rail (250): delivery fees to be paid to the partner + recorded payments. */
export async function fetchPartnerClientLedger(
  partnerId: string,
  tenantId: string
): Promise<PartnerClientLedger> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_client_ledger_get", {
    p_partner_id: partnerId,
    p_tenant_id: tenantId,
  });
  if (error) throw error;
  const row = asRecord(data) ?? {};
  const totals = asRecord(row.totals) ?? {};
  const entries = Array.isArray(row.entries) ? row.entries : [];
  const payments = Array.isArray(row.payments) ? row.payments : [];
  return {
    tenant_id: str(row.tenant_id, tenantId),
    totals: {
      orders: num(totals.orders),
      delivery_due_total: num(totals.delivery_due_total),
      rejected_due_total: num(totals.rejected_due_total),
      fees_total: num(totals.fees_total),
      paid_total: num(totals.paid_total),
      balance_to_pay: num(totals.balance_to_pay),
    },
    entries: entries
      .map((item) => asRecord(item))
      .filter((r): r is Record<string, unknown> => !!r)
      .map((r) => ({
        order_id: str(r.order_id),
        display_number: nullableStr(r.display_number),
        status: str(r.status),
        return_to_store: bool(r.return_to_store),
        created_at: str(r.created_at),
        completed_at: nullableStr(r.completed_at),
        rejected_at: nullableStr(r.rejected_at),
        delivery_fee: num(r.delivery_fee),
        delivery_fee_responsibility: nullableStr(r.delivery_fee_responsibility),
        delivery_fee_cash_to_driver: bool(r.delivery_fee_cash_to_driver),
        due_amount: num(r.due_amount),
      })),
    payments: payments
      .map((item) => asRecord(item))
      .filter((r): r is Record<string, unknown> => !!r)
      .map((r) => ({
        id: str(r.id),
        amount: num(r.amount),
        note: nullableStr(r.note),
        created_at: str(r.created_at),
      })),
  };
}

/** 250: record a payment received from the client (reduces the dues). */
export async function recordPartnerClientPayment(
  partnerId: string,
  tenantId: string,
  amount: number,
  note?: string
): Promise<{ id: string; amount: number }> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_client_payment_record", {
    p_partner_id: partnerId,
    p_tenant_id: tenantId,
    p_amount: amount,
    p_note: note?.trim() || null,
  });
  if (error) throw error;
  const row = asRecord(data) ?? {};
  return { id: str(row.id), amount: num(row.amount) };
}

// ── Fleet driver wallets (internal_driver_wallets, v2) ────────────────────────

export interface PartnerDriverWalletV2Row {
  driver_id: string;
  name: string;
  phone: string | null;
  is_active: boolean;
  relationship_type: string;
  trip_fee_amount: number;
  /** + = driver owes (cash to hand over); − = payout due. */
  balance: number;
  cash_held: number;
  payout_due: number;
  updated_at: string | null;
  /** 245 breakdown (derived from orders + settlements + ledger). */
  trips: number;
  driver_income: number;
  owe_partner_delivery: number;
  owe_partner_order: number;
  partner_owes_driver: number;
  handover_total: number;
  payout_total: number;
}

export async function listPartnerDriverWalletsV2(
  partnerId: string
): Promise<PartnerDriverWalletV2Row[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_driver_wallets_v2_list", {
    p_partner_id: partnerId,
  });
  if (error) throw error;
  const row = asRecord(data) ?? {};
  const list = Array.isArray(row.drivers) ? row.drivers : [];
  return list
    .map((item) => asRecord(item))
    .filter((r): r is Record<string, unknown> => !!r)
    .map((r) => ({
      driver_id: str(r.driver_id),
      name: str(r.name, "Driver"),
      phone: nullableStr(r.phone),
      is_active: bool(r.is_active),
      relationship_type: str(r.relationship_type),
      trip_fee_amount: num(r.trip_fee_amount),
      balance: num(r.balance),
      cash_held: num(r.cash_held),
      payout_due: num(r.payout_due),
      updated_at: nullableStr(r.updated_at),
      trips: num(r.trips),
      driver_income: num(r.driver_income),
      owe_partner_delivery: num(r.owe_partner_delivery),
      owe_partner_order: num(r.owe_partner_order),
      partner_owes_driver: num(r.partner_owes_driver),
      handover_total: num(r.handover_total),
      payout_total: num(r.payout_total),
    }));
}

export interface PartnerDriverWalletV2Settlement {
  order_id: string;
  delivery_fee: number;
  cash_collected: number;
  driver_earnings: number;
  platform_commission: number;
  net_wallet_delta: number;
  order_status: string | null;
  created_at: string;
  order_value: number;
  order_value_collected: boolean;
}

/** 245: derived wallet components (income vs dues; never legacy rails). */
export interface PartnerDriverWalletBreakdown {
  trips: number;
  driver_income: number;
  owe_partner_delivery: number;
  owe_partner_order: number;
  partner_owes_driver: number;
  partner_owes_driver_unpaid_earnings: number;
  partner_owes_driver_rejection: number;
  /** 246: outstanding (net of scoped clearing). */
  owe_partner_delivery_outstanding: number;
  owe_partner_order_outstanding: number;
  partner_owes_driver_outstanding: number;
  handover_total: number;
  handover_delivery_total: number;
  handover_order_total: number;
  payout_total: number;
  adjustment_total: number;
  net_position: number;
}

export interface PartnerDriverWalletV2Detail {
  driver: {
    id: string;
    name: string;
    phone: string | null;
    is_active: boolean;
    relationship_type: string;
    trip_fee_amount: number;
  };
  wallet: {
    balance: number;
    currency: string;
    updated_at: string | null;
    cash_held: number;
    payout_due: number;
  };
  transactions: PartnerWalletV2Transaction[];
  settlements: PartnerDriverWalletV2Settlement[];
  breakdown: PartnerDriverWalletBreakdown;
}

function mapBreakdown(value: unknown): PartnerDriverWalletBreakdown {
  const r = asRecord(value) ?? {};
  return {
    trips: num(r.trips),
    driver_income: num(r.driver_income),
    owe_partner_delivery: num(r.owe_partner_delivery),
    owe_partner_order: num(r.owe_partner_order),
    partner_owes_driver: num(r.partner_owes_driver),
    partner_owes_driver_unpaid_earnings: num(r.partner_owes_driver_unpaid_earnings),
    partner_owes_driver_rejection: num(r.partner_owes_driver_rejection),
    owe_partner_delivery_outstanding:
      r.owe_partner_delivery_outstanding != null
        ? num(r.owe_partner_delivery_outstanding)
        : num(r.owe_partner_delivery),
    owe_partner_order_outstanding:
      r.owe_partner_order_outstanding != null
        ? num(r.owe_partner_order_outstanding)
        : num(r.owe_partner_order),
    partner_owes_driver_outstanding:
      r.partner_owes_driver_outstanding != null
        ? num(r.partner_owes_driver_outstanding)
        : num(r.partner_owes_driver),
    handover_total: num(r.handover_total),
    handover_delivery_total: num(r.handover_delivery_total),
    handover_order_total: num(r.handover_order_total),
    payout_total: num(r.payout_total),
    adjustment_total: num(r.adjustment_total),
    net_position: num(r.net_position),
  };
}

function mapDriverWalletDetail(data: unknown): PartnerDriverWalletV2Detail {
  const row = asRecord(data) ?? {};
  const driver = asRecord(row.driver) ?? {};
  const wallet = asRecord(row.wallet) ?? {};
  const settlements = Array.isArray(row.settlements) ? row.settlements : [];
  return {
    driver: {
      id: str(driver.id),
      name: str(driver.name, "Driver"),
      phone: nullableStr(driver.phone),
      is_active: bool(driver.is_active),
      relationship_type: str(driver.relationship_type),
      trip_fee_amount: num(driver.trip_fee_amount),
    },
    wallet: {
      balance: num(wallet.balance),
      currency: str(wallet.currency, "IQD"),
      updated_at: nullableStr(wallet.updated_at),
      cash_held: num(wallet.cash_held),
      payout_due: num(wallet.payout_due),
    },
    transactions: mapTransactions(row.transactions),
    settlements: settlements
      .map((item) => asRecord(item))
      .filter((r): r is Record<string, unknown> => !!r)
      .map((r) => ({
        order_id: str(r.order_id),
        delivery_fee: num(r.delivery_fee),
        cash_collected: num(r.cash_collected),
        driver_earnings: num(r.driver_earnings),
        platform_commission: num(r.platform_commission),
        net_wallet_delta: num(r.net_wallet_delta),
        order_status: nullableStr(r.order_status),
        created_at: str(r.created_at),
        order_value: num(r.order_value),
        order_value_collected: bool(r.order_value_collected),
      })),
    breakdown: mapBreakdown(row.breakdown),
  };
}

export async function fetchPartnerDriverWalletV2(
  partnerId: string,
  driverId: string
): Promise<PartnerDriverWalletV2Detail> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_driver_wallet_v2_get", {
    p_partner_id: partnerId,
    p_driver_id: driverId,
  });
  if (error) throw error;
  return mapDriverWalletDetail(data);
}

export type PartnerDriverSettleKind = "handover" | "payout";
export type PartnerDriverSettleScope = "delivery" | "order";

/**
 * handover — driver returns collected cash (requires cash on hand).
 * payout   — partner pays what is owed (requires an outstanding due).
 * scope    — handover only: clear the delivery margin or the order-value
 *            component explicitly (246); omit for the net position.
 * Returns the refreshed detail snapshot.
 */
export async function settlePartnerDriverWalletV2(
  partnerId: string,
  driverId: string,
  kind: PartnerDriverSettleKind,
  amount: number,
  note?: string,
  scope?: PartnerDriverSettleScope | null
): Promise<PartnerDriverWalletV2Detail> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_driver_wallet_v2_settle", {
    p_partner_id: partnerId,
    p_driver_id: driverId,
    p_kind: kind,
    p_amount: amount,
    p_note: note?.trim() || null,
    p_scope: scope ?? null,
  });
  if (error) throw error;
  return mapDriverWalletDetail(data);
}

/**
 * topup — partner adds credit to the driver wallet (247). Recorded as an
 * adjustment on the net balance (positive = the driver holds more cash).
 * Positive amounts only; use scoped handovers for downward corrections.
 * Returns the refreshed detail snapshot.
 */
export async function topupPartnerDriverWalletV2(
  partnerId: string,
  driverId: string,
  amount: number,
  note?: string
): Promise<PartnerDriverWalletV2Detail> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_driver_wallet_v2_topup", {
    p_partner_id: partnerId,
    p_driver_id: driverId,
    p_amount: amount,
    p_note: note?.trim() || null,
  });
  if (error) throw error;
  return mapDriverWalletDetail(data);
}

const WALLET_TX_TYPES = new Set([
  "topup",
  "platform_fee",
  "adjustment",
  "refund",
  "cash_collected",
  "trip_earnings",
  "cash_handover",
  "payout",
  "order_value_collected",
  "rejection_refund",
  "rejection_fee_keep",
]);

/** Label key for a v2 wallet transaction type (falls back to a generic label). */
export function walletTxTypeKey(
  type: string,
  description?: string | null,
): TranslationKey {
  if (
    type === "cash_handover" &&
    description === "Partner margin — deducted from balance"
  ) {
    return "walletV2.txType.partner_margin";
  }
  const normalized = WALLET_TX_TYPES.has(type) ? type : "other";
  return `walletV2.txType.${normalized}` as TranslationKey;
}

/** Map engine-written ledger descriptions to localized keys (raw text kept otherwise, e.g. operator notes). */
const WALLET_TX_DESCRIPTIONS: Record<string, TranslationKey> = {
  "Cash collected at door": "walletV2.txDesc.cashCollected",
  "Order value collected at door": "walletV2.txDesc.orderValueCollected",
  "Earned delivery fee": "walletV2.txDesc.tripEarnings",
  "Partner delivery share (auto)": "walletV2.txDesc.partnerShareAuto",
  "Partner margin — deducted from balance": "walletV2.txDesc.marginDeducted",
  "Delivery fee received": "walletV2.txDesc.feeReceived",
  "Platform usage fee": "walletV2.txDesc.platformFee",
  "Cash handed over": "walletV2.txDesc.cashHandedOver",
  "Payout to driver": "walletV2.txDesc.payoutToDriver",
  "Wallet top-up": "walletV2.txDesc.topup",
  "Rejected order — refund of paid-at-cashier order value":
    "walletV2.txDesc.rejectionRefund",
  "Rejected order — driver fee kept by partner decision":
    "walletV2.txDesc.rejectionFeeKeep",
};

export function walletTxDescriptionKey(
  description: string | null | undefined,
): TranslationKey | null {
  if (!description) return null;
  return WALLET_TX_DESCRIPTIONS[description] ?? null;
}

// ── Error → i18n key mapping ─────────────────────────────────────────────────

/** Map v2 wallet RPC failures to i18n keys — call `t(mapWalletV2Error(err))`. */
export function mapWalletV2Error(err: unknown): TranslationKey {
  const msg =
    err && typeof err === "object" && "message" in err
      ? String((err as { message: unknown }).message)
      : String(err ?? "");
  if (msg.includes("wallet_dues_unsettled")) return "errors.walletDuesUnsettled";
  if (msg.includes("insufficient_cash_on_hand")) return "errors.walletCashExceeds";
  if (msg.includes("payout_exceeds_due")) return "errors.walletPayoutExceeds";
  if (msg.includes("scope_exceeds_outstanding")) return "errors.walletScopeExceeds";
  if (msg.includes("invalid_settle_scope")) return "errors.walletScopeInvalid";
  if (msg.includes("invalid_settle_kind")) return "errors.walletKindInvalid";
  if (msg.includes("invalid_amount")) return "errors.amountInvalid";
  if (msg.includes("amount_too_large")) return "errors.amountTooLarge";
  if (msg.includes("partner_driver_not_in_fleet")) return "errors.driverNotInFleet";
  if (msg.includes("client_not_found")) return "errors.partnerClientNotFound";
  if (msg.includes("insufficient_partner_role")) return "errors.roleCannotChange";
  if (msg.includes("not_partner_user") || msg.includes("not_authenticated")) {
    return "errors.partnerAccessDenied";
  }
  return "clientWallet.failed";
}
